/**
 * LandlordRenewalInbox — dashboard section that shows every renewal
 * offer this landlord has sent, grouped by status. Sits below the
 * BulkLeaseRenewal card so landlords immediately see how tenants
 * responded to their bulk actions.
 *
 * Uses `GET /api/landlord/renewal-offers` which returns items + counts:
 *   { items: [...], counts: { open, accepted, countered, declined } }
 */
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, MessageSquare, RefreshCw, ArrowRight, Inbox } from "lucide-react";
import { api } from "@/lib/api";

const money = (cents) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format((Number(cents) || 0) / 100);
const STATUS_TONES = {
  open: { bg: "bg-slate-50", pill: "bg-slate-200 text-slate-700", icon: Inbox, label: "Awaiting tenant" },
  accepted: { bg: "bg-emerald-50", pill: "bg-emerald-200 text-emerald-800", icon: CheckCircle2, label: "Accepted" },
  countered: { bg: "bg-amber-50", pill: "bg-amber-200 text-amber-900", icon: MessageSquare, label: "Countered" },
  declined: { bg: "bg-rose-50", pill: "bg-rose-200 text-rose-800", icon: XCircle, label: "Declined" },
};

export default function LandlordRenewalInbox() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/landlord/renewal-offers");
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-2 text-slate-500 text-sm">
        <RefreshCw className="w-4 h-4 animate-spin"/> Loading renewal inbox…
      </div>
    );
  }

  const counts = data?.counts || { open: 0, accepted: 0, countered: 0, declined: 0 };
  const items = data?.items || [];
  const total = counts.open + counts.accepted + counts.countered + counts.declined;

  // Don't render an empty inbox — no point showing "0 renewal offers"
  // when the landlord hasn't run a bulk renewal yet.
  if (total === 0) return null;

  const filtered = tab === "all" ? items : items.filter((o) => (o.status || "open").toLowerCase() === tab);

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="landlord-renewal-inbox">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1.5">
            <Inbox className="w-3 h-3"/> Renewal inbox
          </div>
          <h3 className="font-display text-lg font-bold text-slate-900 mt-1">Tenant responses to your renewals</h3>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="renewal-inbox-refresh"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900"
        >
          <RefreshCw className="w-3.5 h-3.5"/> Refresh
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="renewal-inbox-tabs">
        <FilterPill active={tab === "all"} onClick={() => setTab("all")} count={total} label="All" testid="tab-all"/>
        <FilterPill active={tab === "open"} onClick={() => setTab("open")} count={counts.open} label="Awaiting tenant" testid="tab-open"/>
        <FilterPill active={tab === "accepted"} onClick={() => setTab("accepted")} count={counts.accepted} label="Accepted" tone="emerald" testid="tab-accepted"/>
        <FilterPill active={tab === "countered"} onClick={() => setTab("countered")} count={counts.countered} label="Countered" tone="amber" testid="tab-countered"/>
        <FilterPill active={tab === "declined"} onClick={() => setTab("declined")} count={counts.declined} label="Declined" tone="rose" testid="tab-declined"/>
      </div>

      <div className="mt-4 divide-y divide-slate-100 max-h-80 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500">No offers in this bucket.</div>
        ) : filtered.map((o) => {
          const status = (o.status || "open").toLowerCase();
          const tone = STATUS_TONES[status] || STATUS_TONES.open;
          const Icon = tone.icon;
          const diff = (o.new_rent_cents || 0) - (o.old_rent_cents || 0);
          const counter = o.counter_rent_cents;
          return (
            <div key={o.offer_id} className="py-3 flex items-center gap-3" data-testid={`renewal-inbox-row-${o.offer_id}`}>
              <div className={`w-8 h-8 rounded-full ${tone.bg} flex items-center justify-center flex-none`}>
                <Icon className="w-4 h-4"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {o.tenant_email || "Unknown tenant"}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  Lease {o.lease_id?.slice(0, 8)} · ends {o.new_end_date} · {money(o.new_rent_cents)}
                  {diff !== 0 && <span className={diff > 0 ? "text-rose-700" : "text-emerald-700"}> ({diff > 0 ? "+" : ""}{money(diff)})</span>}
                  {counter && status === "countered" && (
                    <span className="text-amber-800 font-semibold"> · Counter: {money(counter)}</span>
                  )}
                </div>
                {o.counter_note && status === "countered" && (
                  <div className="mt-1 text-xs italic text-slate-500 truncate">"{o.counter_note}"</div>
                )}
                {o.decline_reason && status === "declined" && (
                  <div className="mt-1 text-xs italic text-rose-700 truncate">Reason: {o.decline_reason}</div>
                )}
              </div>
              <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${tone.pill} flex-none`}>
                {tone.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, count, label, tone, testid }) {
  const toneCls = tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-800" : tone === "rose" ? "text-rose-700" : "text-slate-700";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${active ? "bg-slate-900 text-white border-slate-900" : `bg-white border-slate-200 ${toneCls} hover:border-slate-300`}`}
    >
      {label} <span className="tabular-nums opacity-80">{count}</span>
    </button>
  );
}
